// Mirror of api/src/utils/cleanMerchantDescription.ts so the UI can pre-clean
// transaction descriptions when suggesting rule keywords without a round trip.
// Keep in sync with the backend file — the backend version is the source of
// truth (used for match-count previews and for any server-side suggestion).

const PROCESSOR_PREFIXES = [
  "SQ *",
  "SQ*",
  "TST*",
  "TST *",
  "PAYPAL *",
  "PAYPAL*",
  "PP*",
  "PP *",
  "SP *",
  "SP*",
  "IC*",
  "IC *",
  "WPY*",
  "WPY *",
  "CKE*",
  "DD *",
  "DD*",
  "GOOGLE *",
  "GOOGLE*",
  "AMZN MKTP",
  "AMAZON MKTPL",
  "AMAZON MKTPLACE",
  "APPLE.COM/BILL",
];

const STRIP_PATTERNS: RegExp[] = [
  /\bCard\s+\d{3,5}\b/gi,
  /\bCard\s+(?:Purchase|Payment|Debit|Credit)(?:\s+Returned)?\b/gi,
  /\b\d{4}[-/]\d{1,2}[-/]\d{1,2}\b/g,
  /\b\d{1,2}[-/]\d{1,2}(?:[-/]\d{2,4})?\b/g,
  /\b\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?\b/gi,
  /\b\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
  /\b\d{3}[-.]\d{4}\b/g,
  /\b\d{6,}\b/g,
  /#\s?\d{1,6}\b/g,
  /\b(?:STORE|STR)\s+\d{1,6}\b/gi,
  /\b(?:POS\s+DEBIT|POS\s+PURCHASE|DEBIT\s+CARD\s+PURCHASE|CHECKCARD|CHECK\s+CARD|RECURRING\s+PAYMENT|RECURRING|PURCHASE\s+AUTHORIZED\s+ON|PURCHASE\s+AUTH|PURCHASE|PAYMENT|AUTH|XXXXXX\d*)\b/gi,
  /\s+\b[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?\s+(?:AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)\b(?=\s+\d|\s*$|\s+(?:US|USA)\b)/g,
  /\s+\b[A-Z]{2}\s+(?:US|USA)\s*$/g,
  /\s+\b[A-Z]{2}\s*$/g,
  /\s+\b(?:US|USA)\s*$/g,
  /\s\d{3,5}(?=\s|$)/g,
];

const US_STATE_CODES = new Set([
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
]);

function stripProcessorPrefix(s: string): string {
  const upper = s.toUpperCase();
  for (const prefix of PROCESSOR_PREFIXES) {
    if (upper.startsWith(prefix)) {
      return s.slice(prefix.length).trimStart();
    }
  }
  return s;
}

function trimTrailingWhitespaceAndPunct(s: string): string {
  return s.replace(/[\s,.\-*#]+$/g, "").replace(/^[\s,.\-*#]+/g, "");
}

export function cleanMerchantDescription(
  input: string | null | undefined
): string {
  if (!input || typeof input !== "string") return "";
  let s = input.trim();
  if (!s) return "";
  s = stripProcessorPrefix(s);
  for (const re of STRIP_PATTERNS) {
    s = s.replace(re, " ");
  }
  s = s.replace(/\s[()*\-]+\s/g, " ").replace(/\s[()*\-]+$/g, "");
  s = s.replace(/\s+/g, " ").trim();
  const tokens = s.split(" ");
  while (
    tokens.length > 1 &&
    US_STATE_CODES.has(tokens[tokens.length - 1].toUpperCase())
  ) {
    tokens.pop();
  }
  s = tokens.join(" ");
  s = trimTrailingWhitespaceAndPunct(s);
  return s;
}

export function suggestRuleKeyword(
  input: string | null | undefined
): string {
  const cleaned = cleanMerchantDescription(input);
  if (!cleaned) return "";
  const alphaTokens = cleaned
    .split(/\s+/)
    .filter((t) => /[a-zA-Z]/.test(t))
    .slice(0, 4);
  if (alphaTokens.length === 0) return "";
  return alphaTokens.join(" ").toLowerCase();
}
